import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getSeriesColor, getSeriesPattern } from './color-palette';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const WIDTH = 600;
const HEIGHT = 400;

/**
 * Vertical bar chart using D3.js for SVG rendering.
 * Supports keyboard navigation, tooltips, and accessibility patterns.
 */
export function BarChart({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handleBarInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({
        dimensionValue,
        measureValue,
        dimensionLabel,
        measureLabel,
        x: targetRect.left - rect.left + targetRect.width / 2,
        y: targetRect.top - rect.top,
      });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg
      .append('g')
      .attr('class', 'chart-content')
      .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.dimensionValue))
      .range([0, innerWidth])
      .padding(0.2);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.measureValue) ?? 0])
      .nice()
      .range([innerHeight, 0]);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('transform', 'rotate(-30)')
      .style('text-anchor', 'end')
      .attr('font-size', '12px');

    // Y axis
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '12px');

    // Axis labels
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + MARGIN.bottom - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(dimensionLabel);

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -MARGIN.left + 15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(measureLabel);

    // Bars with patterns
    data.forEach((d) => {
      const color = getSeriesColor(d.dimensionValue);
      const patternId = getSeriesPattern(d.dimensionValue);
      const x = xScale(d.dimensionValue) ?? 0;
      const y = yScale(d.measureValue);
      const barWidth = xScale.bandwidth();
      const barHeight = innerHeight - y;

      // Background bar with solid color
      g.append('rect')
        .attr('x', x)
        .attr('y', y)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', color);

      // Overlay pattern for accessibility
      const patternGroup = g.append('g').attr('style', `color: ${color}`);
      patternGroup
        .append('rect')
        .attr('x', x)
        .attr('y', y)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', `url(#${patternId})`)
        .attr('opacity', 0.3);
    });
  }, [data, dimensionLabel, measureLabel]);

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">
        No data available
      </div>
    );
  }

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

  const xScale = d3
    .scaleBand()
    .domain(data.map((d) => d.dimensionValue))
    .range([0, innerWidth])
    .padding(0.2);

  const yScale = d3
    .scaleLinear()
    .domain([0, d3.max(data, (d) => d.measureValue) ?? 0])
    .nice()
    .range([innerHeight, 0]);

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Bar chart: ${dimensionLabel} by ${measureLabel}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full max-w-[600px]"
        aria-hidden="true"
      >
        <ChartPatternDefs />
      </svg>
      {/* Invisible keyboard-navigable overlays for each bar */}
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {data.map((d) => {
          const x = (MARGIN.left + (xScale(d.dimensionValue) ?? 0)) / WIDTH * 100;
          const y = (MARGIN.top + yScale(d.measureValue)) / HEIGHT * 100;
          const barWidth = xScale.bandwidth() / WIDTH * 100;
          const barHeight = (innerHeight - yScale(d.measureValue)) / HEIGHT * 100;

          return (
            <button
              key={d.dimensionValue}
              type="button"
              role="listitem"
              className="absolute border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: `${barWidth}%`,
                height: `${barHeight}%`,
              }}
              tabIndex={0}
              aria-label={`${dimensionLabel}: ${d.dimensionValue}, ${measureLabel}: ${d.measureValue}`}
              onMouseEnter={(e) => handleBarInteraction(d.dimensionValue, d.measureValue, e)}
              onFocus={(e) => handleBarInteraction(d.dimensionValue, d.measureValue, e)}
              onMouseLeave={hideTooltip}
              onBlur={hideTooltip}
              onClick={() => onSegmentClick?.(d.dimensionValue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSegmentClick?.(d.dimensionValue);
                }
              }}
            />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
